<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import DateTimePicker from '@/shared/components/DateTimePicker.vue';
    import { InputRegexValidator } from '@/shared/helpers/inputRegexValidator.ts';
    import type { AttributeType } from '@/shared/helpers/api/types/entities.ts';

    const props = defineProps<{
        attributeType: AttributeType;
        regex: string | null;
        regexErrorMessage: string | null;
        existingChoices: string[];
    }>();

    const emit = defineEmits<{
        confirm: [value: string];
        cancel: [];
    }>();

    const value = ref('');
    const hasInteracted = ref(false);

    function onDateTimePicked(payload: string) {
        value.value = payload;
    }

    watch(value, () => {
        hasInteracted.value = true;
    });

    const errorMessage = computed<string | null>(() => {
        const v = String(value.value ?? '').trim();

        if (!v) {
            return props.attributeType !== 'DateTime'
                && props.attributeType !== 'Date'
                && hasInteracted.value
                ? 'Value cannot be empty'
                : null;
        }

        if (props.existingChoices.includes(v)) {
            return 'This choice already exists';
        }

        if (props.attributeType === 'Int') {
            if (!/^-?\d+$/.test(v)) {
                return 'Value must be a valid integer (digits only, optional leading minus)';
            }
            const num = Number(v);
            if (num < -2147483648 || num > 2147483647) {
                return 'Value must be between -2147483648 and 2147483647';
            }
        } else if (props.attributeType === 'Float') {
            if (!/^-?(\d+\.?\d*|\d*\.?\d+)$/.test(v) || !isFinite(Number(v))) {
                return 'Value must be a valid number';
            }
        } else if (props.attributeType === 'Uri') {
            try {
                new URL(v);
            } catch {
                return 'Value must be a valid absolute URI';
            }
        } else if (props.attributeType === 'String' && props.regex) {
            const validator = new InputRegexValidator(new RegExp(props.regex), props.regexErrorMessage);
            const result = validator.validate(v);
            if (result?.error) {
                return result.error;
            }
        }

        return null;
    });

    const canSubmit = computed(() => {
        const v = String(value.value ?? '').trim();
        if (!v) return false;
        if (props.existingChoices.includes(v)) return false;
        return !errorMessage.value;
    });

    function onSubmit() {
        if (canSubmit.value) {
            emit('confirm', String(value.value).trim());
        }
    }

    function onCancel() {
        emit('cancel');
    }
</script>

<template>
    <modal-base
        @on-cancel="onCancel">
        <template #modal-header>
            New attribute value choice
        </template>
        <template #modal-body>
            <b-field
                :label="`Value (${props.attributeType})`"
                :type="errorMessage ? 'is-danger' : undefined"
                :message="errorMessage">
                <date-time-picker
                    v-if="props.attributeType === 'Date' || props.attributeType === 'DateTime'"
                    :input="value"
                    :date-only="props.attributeType === 'Date'"
                    @dt-picker="onDateTimePicked" />
                <b-input
                    v-else
                    v-model="value" />
            </b-field>
        </template>
        <template #modal-footer>
            <div class="buttons">
                <b-button
                    label="Cancel"
                    @click="onCancel" />
                <b-button
                    label="Add"
                    type="is-primary"
                    :disabled="!canSubmit"
                    @click="onSubmit" />
            </div>
        </template>
    </modal-base>
</template>

<style scoped>
:deep(.modal-card-body) {
    overflow: visible;
}

:deep(.modal-card) {
    overflow: visible;
}
</style>
